// src/utils/adminAuth.ts
import unifiedAuth from './unifiedAuth';

export const adminAuth = {
  /**
   * Check if the current user is authenticated and has admin privileges
   */
  isAdminAuthenticated: (): boolean => {
    // First check if user is authenticated
    if (!unifiedAuth.isAuthenticated()) {
      return false;
    }

    // Check if user data exists
    const user = unifiedAuth.user.getUser() as Record<string, unknown>;
    if (!user) {
      return false;
    }

    // Check for admin privileges - support both backend property formats
    return Boolean(
      user.isAdmin || user.admin || user.is_staff || user.is_superuser || 
      user.is_admin_user || user.isAdminUser || user.isStaff || user.isSuperuser
    );
  },

  /**
   * Ensure admin authentication and redirect to login if not authenticated
   */
  requireAdminAuth: (): boolean => {
    // Debug logging to help troubleshoot admin access issues
    // Debug info for admin auth - removed for production
    
    if (!adminAuth.isAdminAuthenticated()) {
      // Don't automatically clear tokens - user might just not have admin privileges
      // Only redirect to home page or show an access denied message
      if (typeof window !== 'undefined') {
        // Instead of clearing tokens and forcing re-login, just show access denied
        alert('Access denied. You do not have administrator privileges.');
        window.location.href = '/';
      }
      return false;
    }
    return true;
  },

  /**
   * Get admin user info
   */
  getAdminUser: () => {
    const user = unifiedAuth.user.getUser();
    if (!user || !adminAuth.isAdminAuthenticated()) {
      return null;
    }
    return user;
  },

  /**
   * Login specifically for admin users
   */
  adminLogin: async (email: string, password: string) => {
    try {
      const result = await unifiedAuth.auth.login(email, password);
      
      // Verify admin permissions after login
      if (!adminAuth.isAdminAuthenticated()) {
        throw new Error('Account does not have admin privileges');
      }
      
      return result;
    } catch (error) {
      // Clear tokens on failed admin login
      unifiedAuth.clearTokens();
      unifiedAuth.user.clearUser();
      throw error;
    }
  }
};

export default adminAuth;